import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import {
  ShieldCheck,
  Heart,
  Award,
  Sparkles,
  Brain,
  Stethoscope,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import SEOHead from "@/components/SEOHead";
import AuthorityChain from "@/components/AuthorityChain";
import {
  RelatedPages,
  allTreatmentsLinks,
  allServiceLinks,
  allTeamLinks,
} from "@/components/RelatedPages";
const teamMembers = [
  {
    name: "Dr. Daniel Pietras, MD",
    role: "Medical Director & Board-Certified Psychiatrist",
    specialty:
      "Treatment-Resistant Depression, Neuromodulation & Biopsychosocial Care",
    bio: "Board-certified by the American Board of Psychiatry and Neurology with extensive experience in personalized, evidence-based psychiatric care and advanced neuromodulation.",
    image:
      "https://vibe.filesafe.space/1787327131284421342/attachments/e318e72d-901e-4c29-933c-a9d8e45ee707.jpg",
    credentials: [
      "Board Certified Psychiatrist",
      "ABPN Certified",
      "Centerstone Alumni",
      "Florida Licensed",
    ],
    link: "/dr-daniel-pietras-md",
  },
  {
    name: "Nyoka Powell Robinson, PMHNP-BC",
    role: "Psychiatric Nurse Practitioner",
    specialty: "IV Ketamine Infusion Therapy & Medication Management",
    bio: "Over 7 years of clinical experience in psychiatry, addiction recovery, and family care. Specializes in building trust with diverse communities, IV ketamine protocols, and medication management.",
    image:
      "https://vibe.filesafe.space/1787327131284421342/attachments/220957c6-b29b-4bf7-8db5-75061b091930.jpg",
    credentials: [
      "Board Certified PMHNP-BC",
      "IV Ketamine Specialist",
      "7+ Years Psychiatry Experience",
    ],
    link: "/nyoka-powell-robinson",
  },
  {
    name: "Olivia Sierra, LCSW",
    role: "Primary Therapist",
    specialty: "Nervous System Regulation, Neurofeedback, Sound Healing & NLP",
    bio: "Psychospiritual Therapist combining evidence-based therapies with holistic approaches. Expert in nervous system regulation, sound healing, meditation, and NLP to empower lasting change.",
    image:
      "https://vibe.filesafe.space/1787327131284421342/attachments/cde303f5-c4b3-4ddd-bfa5-fac4ff11d017.jpg",
    credentials: [
      "Licensed Clinical Social Worker",
      "Neurofeedback & NLP Certified",
      "Reiki Master & Sound Healer",
    ],
    link: "/olivia-sierra",
  },
  {
    name: "Timitra Martin, RCSW",
    role: "Primary Therapist",
    specialty: "Trauma-Informed Care, CBT, DBT & Emotional Resilience",
    bio: "Dedicated therapist committed to supporting individuals through a trauma-informed, client-centered approach. Specializes in helping clients navigate anxiety, depression, trauma, and life stressors.",
    image:
      "https://vibe.filesafe.space/1787327131284421342/attachments/69d98d13-c27d-4eba-8601-cc7105501c10.jpg",
    credentials: [
      "Registered Clinical Social Worker",
      "CBT & DBT Specialist",
      "Trauma-Informed Care",
    ],
    link: "/timitra-martin",
  },
  {
    name: "Roxana Carmenate, LCSW",
    role: "Primary Therapist",
    specialty: "EMDR Intensives, Ketamine/Spravato Therapy & Trauma Recovery",
    bio: "Over 20 years of clinical experience. Specializes in Ketamine/Spravato-assisted EMDR intensives, trauma/PTSD, depression, grief, and faith-based counseling to help patients return to what they love.",
    image:
      "https://vibe.filesafe.space/1787327131284421342/attachments/ee84342a-be8e-47a0-abb0-244d1136ac31.jpg",
    credentials: [
      "Licensed Clinical Social Worker",
      "EMDR Intensives",
      "Ketamine/Spravato Therapy",
      "20+ Yrs Experience",
    ],
    link: "/roxana-carmenate",
  },
];
const coreValues = [
  {
    icon: Heart,
    title: "Compassionate Excellence",
    description:
      "Every patient is listened to with deep empathy, dignity, and personal dedication throughout their healing journey.",
  },
  {
    icon: Brain,
    title: "Cutting-Edge Innovation",
    description:
      "We bring Stanford-developed SAINT® TMS, standard TMS, and Spravato® directly to South Florida patients.",
  },
  {
    icon: ShieldCheck,
    title: "In-Network Accessibility",
    description:
      "Proudly in-network with Medicare, Veterans Administration (VA), and major insurance plans to ensure accessible care.",
  },
  {
    icon: Award,
    title: "Proven Clinical Outcomes",
    description:
      "Over 83% of our TMS patients experience significant clinical response or complete remission from severe depression.",
  },
];
const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"About Us | Neuro Wellness TMS"}
        description={
          "Meet the medical team at Neuro Wellness TMS Centers of America in Coral Springs, FL. Board-certified psychiatrists and clinicians specializing in treatment-resistant depression, TMS, SAINT TMS, SPRAVATO®, and advanced neuromodulation therapy."
        }
        canonical={"/about"}
        keywords={
          "Neuro Wellness TMS Centers of America, Coral Springs psychiatric clinic, depression specialists South Florida, board-certified psychiatrist Coral Springs, TMS treatment team, neuromodulation experts"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Neuro Wellness TMS Centers of America",
          description:
            "Learn about our psychiatric practice specializing in treatment-resistant depression in Coral Springs, FL.",
          url: "/about",
          publisher: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* About Hero Section */}
        <section className="relative overflow-hidden bg-gradient-hero py-16 sm:py-24">
          <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-light-blue/50 blur-3xl" />
          <div className="container-px mx-auto max-w-7xl">
            <div className="max-w-3xl space-y-6 animate-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/10 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-3.5 w-3.5 text-accent" />
                <span>About Neuro Wellness TMS Centers of America</span>
              </div>
              <h1 className="font-headings text-[2rem] font-extrabold text-primary sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                Dedicated to Restoring Hope & Transforming Brain Health
              </h1>
              <p className="text-base sm:text-xl text-foreground/80 leading-relaxed font-normal">
                Neuro Wellness TMS Centers of America is an outpatient
                psychiatric neuromodulation center in Coral Springs, Florida
                providing advanced treatment options for depression, including
                SAINT® TMS, conventional TMS, SPRAVATO®, and accelerated
                neuromodulation options. Our mission is to help individuals
                living with treatment-resistant depression access FDA-cleared,
                non-invasive solutions.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story, Mission, Vision, and Values */}
        <section className="py-16 sm:py-24 bg-card">
          <div className="container-px mx-auto max-w-7xl space-y-16">
            {/* Top Grid: Clinic Overview */}
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
                  <Stethoscope className="h-4 w-4" />
                  <span>Our Practice & Philosophy</span>
                </div>
                <h2 className="font-headings text-2xl font-bold text-primary sm:text-4xl">
                  Neuro Wellness TMS Centers of America
                </h2>
                <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
                  The team at Neuro Wellness TMS Centers of America has a
                  passion for helping their patients cultivate a more joyful and
                  fulfilling life with complete psychiatric services offered in
                  a comfortable environment. Located in Coral Springs, Florida,
                  we proudly serve the Broward, Miami-Dade, and Palm Beach
                  county communities.
                </p>
                <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
                  When patients enter Neuro Wellness TMS Centers of America,
                  they’re welcomed by a team of qualified experts who treat
                  patients with compassion and the utmost care. Our physicians
                  aim to establish trusting relationships with patients while
                  providing high-quality, comprehensive care.
                </p>
                <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
                  We are experienced in evaluating and treating a wide range of
                  psychiatric conditions including mood disorders, bipolar
                  disorder, anxiety disorders, depression, obsessive-compulsive
                  disorder (OCD), autism spectrum disorders, post-traumatic
                  stress disorder (PTSD), attention-deficit disorder (ADD), and
                  attention-deficit hyperactivity disorder (ADHD).
                </p>

                <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                    <span>In-Network VA Provider</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                    <span>Medicare & Major Insurance</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                    <span>Stanford SAINT® Protocol</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                    <span>SmartFocus® Neuro-Navigated TMS</span>
                  </div>
                </div>
              </div>

              {/* Clinic Atmosphere Showcase Card */}
              <div className="relative rounded-2xl bg-gradient-navy p-8 text-primary-foreground shadow-premium overflow-hidden space-y-6">
                <div className="pointer-events-none absolute -bottom-10 -right-10 h-60 w-60 rounded-full bg-accent/20 blur-2xl" />
                <h3 className="font-headings text-xl font-bold text-white">
                  Advanced Neuromodulation Precision
                </h3>
                <p className="text-sm text-primary-foreground/85 leading-relaxed">
                  Neuro Wellness is a Verified SAINT® Provider — listed on
                  Magnus Medical's official provider locator — and the only
                  verified outpatient SAINT® provider on the East Coast. We
                  deliver the FDA-cleared, fMRI-guided SAINT® protocol on the
                  Magnus Neuromodulation System, along with conventional TMS,
                  SPRAVATO®, and other advanced neuromodulation options for
                  treatment-resistant depression.
                </p>
                <div className="p-4 rounded-xl bg-white/10 border border-white/15 backdrop-blur-xs">
                  <p className="text-sm italic text-accent font-medium leading-relaxed">
                    “If you live in the Broward, Miami-Dade, or Palm Beach
                    community, it is highly encouraged to make a visit to Neuro
                    Wellness Centers of America. You will not be disappointed.”
                  </p>
                </div>
                <div className="space-y-3 border-t border-white/10 pt-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-primary-foreground/70">
                      Treatments Offered:
                    </span>
                    <span className="font-bold text-accent">
                      TMS, SAINT®, SPRAVATO®, IV Ketamine
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-primary-foreground/70">
                      Patient Demographics:
                    </span>
                    <span className="font-bold text-white">
                      Young Adults to Seniors & Veterans
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission & Vision Section */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-background p-8 shadow-soft space-y-3">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
                  <Award className="h-4 w-4" />
                  <span>Our Mission</span>
                </div>
                <h3 className="font-headings text-2xl font-bold text-primary">
                  Excellence in Patient Care
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Neuro Wellness TMS Centers of America is a progressive
                  psychiatry practice dedicated to healing and excellence with a
                  strong focus on treating depression and neuropsychiatric
                  conditions.
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-background p-8 shadow-soft space-y-3">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
                  <Sparkles className="h-4 w-4" />
                  <span>Our Vision</span>
                </div>
                <h3 className="font-headings text-2xl font-bold text-primary">
                  A Leader in Healthcare Quality
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Our team provides comprehensive and compassionate healthcare
                  service. We honor individuality, empower people through open
                  and honest communication, and strive to earn the loyalty of
                  every person we serve.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values Grid */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container-px mx-auto max-w-7xl">
            <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-4xl">
                Our Values
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Principles that guide every patient interaction and treatment
                plan at Neuro Wellness.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-6 shadow-soft text-center space-y-3">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-light-blue text-accent">
                  <Heart className="h-6 w-6" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Serving Patients as Individuals
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Serving patients as individuals and honoring their humanity
                  throughout their entire treatment journey.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-card p-6 shadow-soft text-center space-y-3">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-light-blue text-accent">
                  <Award className="h-6 w-6" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Excellence in Medical Care
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Delivering evidence-based, cutting-edge psychiatric and
                  neuromodulation therapies with highest precision.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-card p-6 shadow-soft text-center space-y-3">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-light-blue text-accent">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Team Commitment
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Team commitment to respect, open communication, complete
                  confidentiality, and diversity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Medical Team Section */}
        <section
          id="team"
          className="py-16 sm:py-24 bg-card border-t border-border"
        >
          <div className="container-px mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent mb-2">
                <Stethoscope className="h-4 w-4" />
                <span>Expert Physician Care</span>
              </div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-4xl">
                Meet Our Experienced Medical Leadership
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Our team consists of board-certified psychiatrists and
                neuromodulation specialists dedicated to your physical and
                emotional well-being.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
              {teamMembers.map((member, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-2xl border border-border bg-background shadow-soft flex flex-col sm:flex-row"
                >
                  <div className="sm:w-2/5 aspect-[4/3] sm:aspect-auto relative overflow-hidden bg-muted">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  <div className="sm:w-3/5 p-6 sm:p-8 flex flex-col justify-between space-y-4">
                    <div>
                      <h3 className="font-headings text-xl font-bold text-primary">
                        {member.name}
                      </h3>
                      <p className="text-xs font-semibold text-accent uppercase tracking-wider mt-1">
                        {member.role}
                      </p>
                      <p className="text-xs font-medium text-muted-foreground mt-0.5">
                        {member.specialty}
                      </p>
                      <p className="mt-4 text-xs sm:text-sm text-foreground/80 leading-relaxed">
                        {member.bio}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-border flex flex-col gap-3">
                      <div className="flex flex-wrap gap-2">
                        {member.credentials.map((cred, cIdx) => (
                          <span
                            key={cIdx}
                            className="rounded-full bg-light-blue px-2.5 py-1 text-[11px] font-medium text-primary"
                          >
                            {cred}
                          </span>
                        ))}
                      </div>
                      {member.link && (
                        <div>
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="text-xs border-primary/20 text-primary hover:bg-light-blue"
                          >
                            <Link to={member.link}>View Full Profile →</Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="/#consultation">
                  Schedule a Meeting with Our Clinical Team
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Authority chain — Stanford → FDA → Magnus → Verified Provider → Neuro Wellness */}
        <AuthorityChain />

        {/* Consultation Component */}
        <RelatedPages
          heading="Explore Our Treatments & Team"
          links={[...allTreatmentsLinks, ...allServiceLinks, ...allTeamLinks]}
        />
        <Consultation />
      </main>

      <Footer />
    </div>
  );
};
export default About;
